import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Mic2,
  FileAudio,
  TrendingUp,
  BarChart3,
  Wallet,
  CreditCard,
  Settings,
  HelpCircle,
  X,
  Shield,
  Users,
  UsersRound,
  
  ShoppingBag,
  Building2,
  CalendarDays,
  
  Gift,
  Scissors,
  Share2,
  PenSquare,
  AudioLines,
  Mic,
  Waves,
  Link as LinkIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useAdminPanelLink } from "@/hooks/useAdminPanelLink";
import { useShows } from "@/contexts/ShowsContext";
import { useClipStudioAccess } from "@/hooks/useClipStudioAccess";
import { useVoiceStudioAccess } from "@/hooks/useVoiceStudioAccess";

interface PodcasterSidebarProps {
  onNavigate?: () => void;
  showCloseButton?: boolean;
  onClose?: () => void;
}

interface NavItem {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  comingSoon?: boolean;
  beta?: boolean;
  adminOnly?: boolean;
  /** Only shown to users with Clip Studio beta access. */
  clipStudioOnly?: boolean;
  /** Only shown to users with Voice Studio beta access. */
  voiceStudioOnly?: boolean;
  /** Match the route exactly (parent path of nested routes). */
  exact?: boolean;
}



interface NavGroup {
  label?: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    items: [
      { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    ]
  },
  {
    label: "Content",
    items: [
      { to: "/shows", icon: Mic2, label: "Shows" },
      { to: "/episodes", icon: FileAudio, label: "Episodes" },
      { to: "/delivery", icon: TrendingUp, label: "Delivery Status", adminOnly: true },
    ]
  },
  {
    label: "Clip Studio",
    items: [
      { to: "/clip-studio", icon: Scissors, label: "Clip Studio", clipStudioOnly: true, beta: true, exact: true },
      { to: "/clip-studio/social-accounts", icon: Share2, label: "Social Accounts", clipStudioOnly: true, beta: true },
      { to: "/clip-studio/compose", icon: PenSquare, label: "Compose & Post", clipStudioOnly: true, beta: true },
      { to: "/clip-studio/smart-links", icon: LinkIcon, label: "Smart Links", clipStudioOnly: true, beta: true },
      { to: "/clip-studio/performance", icon: BarChart3, label: "Clip Performance", clipStudioOnly: true, beta: true },
    ]
  },
  {
    label: "Voice Studio",
    items: [
      { to: "/voice-studio", icon: AudioLines, label: "Voice Studio", voiceStudioOnly: true, beta: true, exact: true },
      { to: "/voice-studio/episodes", icon: Waves, label: "Rendered Episodes", voiceStudioOnly: true, beta: true },
      { to: "/voice-studio/voices", icon: Mic, label: "Voices", voiceStudioOnly: true, beta: true },
    ]
  },
  {
    label: "Studios",
    items: [
      { to: "/book-studio", icon: Building2, label: "Book A Studio" },
      { to: "/my-bookings", icon: CalendarDays, label: "My Bookings" },
    ]
  },
  {
    label: "Growth",
    items: [
      { to: "/analytics-v2", icon: BarChart3, label: "Analytics" },

      { to: "/referrals", icon: Gift, label: "Referrals" },
    ]
  },

  {
    label: "Financial",
    items: [
      { to: "/wallet", icon: Wallet, label: "Wallet", beta: true },
      { to: "/revenue", icon: TrendingUp, label: "Earnings & Payouts", beta: true },
      
      { to: "/revenue-split", icon: Users, label: "Revenue Splits", beta: true },
    ]
  },
  {
    label: "Account",
    items: [
      { to: "/team", icon: UsersRound, label: "Team" },
      { to: "/billing", icon: CreditCard, label: "Billing" },
      { to: "/settings", icon: Settings, label: "Settings" },
      { to: "/help", icon: HelpCircle, label: "Help & Support" },
    ]
  },
];

function GroupLabel({ label }: { label: string }) {
  return (
    <div className="px-3 py-2">
      <span className="text-xs font-semibold uppercase tracking-wider text-sidebar-muted/70">
        {label}
      </span>
    </div>
  );
}

export function PodcasterSidebar({ onNavigate, showCloseButton, onClose }: PodcasterSidebarProps) {
  const location = useLocation();
  const { showAdminPanel, adminPanelTo, isAdmin } = useAdminPanelLink();
  const { shows } = useShows();
  const hasShows = shows.length > 0;
  const hasClipStudio = useClipStudioAccess();
  const hasVoiceStudio = useVoiceStudioAccess();

  const isActive = (item: NavItem) => {
    if (item.exact) return location.pathname === item.to;
    return location.pathname === item.to ||
      (item.to !== "/dashboard" && location.pathname.startsWith(item.to));
  };

  const isVisible = (item: NavItem) => {
    if (item.to === "/episodes" && !hasShows) return false;
    if (item.adminOnly && !isAdmin) return false;
    if (item.clipStudioOnly && !hasClipStudio) return false;
    if (item.voiceStudioOnly && !hasVoiceStudio) return false;
    return true;
  };

  const visibleGroups = navGroups
    .map((group) => ({ ...group, items: group.items.filter(isVisible) }))
    .filter((group) => group.items.length > 0);

  return (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
      {/* Close Button for drawer mode */}
      {showCloseButton && onClose && (
        <div className="flex h-14 items-center px-4 border-b border-sidebar-border">
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8 text-sidebar-foreground hover:bg-sidebar-accent"
          >
            <X className="h-5 w-5" />
            <span className="sr-only">Close menu</span>
          </Button>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4">
        {visibleGroups.map((group, groupIndex) => (
          <div 
            key={groupIndex} 
            className={cn(
              groupIndex > 0 && "border-t border-sidebar-border pt-3 mt-3"
            )}
          >
            {group.label && <GroupLabel label={group.label} />}
            <div className="space-y-1">
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onNavigate}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                    isActive(item)
                      ? "bg-sidebar-accent text-sidebar-accent-foreground"
                      : "text-sidebar-muted hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                  {item.comingSoon && (
                    <span className="ml-auto rounded-full bg-accent/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase leading-none text-accent">
                      Soon
                    </span>
                  )}
                  {item.beta && (
                    <span className="ml-auto rounded-full bg-accent px-1.5 py-0.5 text-[9px] font-semibold uppercase leading-none text-accent-foreground">
                      Beta
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Admin Panel Link - Visible to platform admins and network admins */}
      {showAdminPanel && (
        <div className="p-4 border-t border-sidebar-border">
          <NavLink
            to={adminPanelTo}
            onClick={onNavigate}
            className={cn(
              "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
              location.pathname.startsWith('/admin')
                ? "bg-sidebar-accent text-sidebar-accent-foreground"
                : "text-sidebar-muted hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
            )}
          >
            <Shield className="h-4 w-4" />
            Admin Panel
          </NavLink>
        </div>
      )}
    </div>
  );
}
